import React, { useRef, useEffect } from 'react';
import { useClientId } from 'shared_components/context/client';
import AliasModal, { AliasForm, AliasId } from './AliasModal';
import { useApi } from 'shared_components/context';
import { DiscographySourceType, useDiscographyIds } from '../useDiscographyIds';
import BandModal, { BandForm, BandId } from './BandModal';

type AliasBandManagementProps = {
  showAliasModal: boolean;
  showBandModal: boolean;
  aliasForm: AliasForm;
  bandForm: BandForm;
  activeAliasTab: DiscographySourceType;
  activeBandTab: DiscographySourceType;
  readonly onClose: () => void;
  readonly onCreateNewAlias: () => void;
  readonly onAliasChange: React.Dispatch<React.SetStateAction<AliasForm>>;
  readonly onBandChange: React.Dispatch<React.SetStateAction<BandForm>>;
  readonly onAliasTabChange: (tab: DiscographySourceType) => void;
  readonly onBandTabChange: (tab: DiscographySourceType) => void;
};

function AliasBandManagement({
  showAliasModal,
  showBandModal,
  aliasForm,
  bandForm,
  activeAliasTab,
  activeBandTab,
  onClose,
  onCreateNewAlias,
  onAliasChange,
  onBandChange,
  onAliasTabChange,
  onBandTabChange,
}: AliasBandManagementProps) {
  const { clientId } = useClientId();
  const { adminApi: api } = useApi();
  const { getCollectedDiscographyIds, collectedDiscographyIds } = useDiscographyIds();
  
  // Track if we're waiting for collectedDiscographyIds to update with a selected band
  const pendingBandIdRef = useRef<string | null>(null);
  
  // When collectedDiscographyIds updates, check if we have a pending band that's now available
  useEffect(() => {
    console.log('=== useEffect: collectedDiscographyIds updated ===');
    console.log('pendingBandIdRef.current:', pendingBandIdRef.current);
    console.log('collectedDiscographyIds.length:', collectedDiscographyIds.length);
    console.log('collectedDiscographyIds:', collectedDiscographyIds);
    
    if (pendingBandIdRef.current && collectedDiscographyIds.length > 0) {
      const pendingBandId = pendingBandIdRef.current;
      console.log('Checking if pending band is now available:', pendingBandId);
      
      const foundBand = collectedDiscographyIds.find(
        (band) => band.id === pendingBandId && band.type === 'group'
      );
      
      console.log('Found band in search:', foundBand);
      console.log('Found band source_ids:', foundBand?.source_ids);
      
      if (foundBand) {
        console.log('Pending band found in collectedDiscographyIds, loading full data:', foundBand);
        loadBandDataIntoForm(foundBand);
        pendingBandIdRef.current = null; // Clear the pending flag
        console.log('Cleared pendingBandIdRef');
      } else {
        console.log('Pending band still not found in collectedDiscographyIds');
        console.log('Available band IDs:', collectedDiscographyIds.map(b => ({ id: b.id, type: b.type })));
      }
    } else {
      console.log('No pending band or collectedDiscographyIds is empty');
    }
  }, [collectedDiscographyIds]);

  const handleAliasFormChange = (updatedItem: AliasId) => {
    onAliasChange((prev) => {
      const updatedIds = [...prev.ids];
      const index = updatedIds.findIndex(
        (item) => item.itemId === updatedItem.itemId
      );

      if (index > -1) {
        updatedIds[index] = {
          ...updatedIds[index],
          ...updatedItem,
        };
      }

      if (!prev.aliasName && prev.manualStageName === false) {
        prev.aliasName = updatedItem.artistName ?? prev.aliasName;
      }

      return {
        ...prev,
        ids: updatedIds,
      };
    });
  };

  const handleBandFormChange = (updatedItem: BandId) => {
    onBandChange((prev) => {
      const updatedIds = [...prev.ids];
      const index = updatedIds.findIndex(
        (item) => item.itemId === updatedItem.itemId
      );

      if (index > -1) {
        updatedIds[index] = {
          ...updatedIds[index],
          ...updatedItem,
        };
      }

      if (!prev.stageName && prev.manualStageName === false) {
        prev.stageName = updatedItem.artistName ?? prev.stageName;
      }

      return {
        ...prev,
        ids: updatedIds,
      };
    });
  };

  const addAliasSourceField = () => {
    onAliasChange((prev) => ({
      ...prev,
      ids: [
        {
          source: activeAliasTab,
          itemId: `${prev.id ? `${prev.id}-` : ''}${activeAliasTab}-${
            prev.ids.length
          }`,
          id: ``,
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        },
        ...prev.ids,
      ],
    }));
  };

  const addBandSourceField = () => {
    onBandChange((prev) => ({
      ...prev,
      ids: [
        {
          source: activeBandTab,
          itemId: `${prev.id ? `${prev.id}-` : ''}${activeBandTab}-${
            prev.ids.length
          }`,
          id: ``,
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        },
        ...prev.ids,
      ],
    }));
  };

  const validateAliasForm = () => {
    const errors: Record<string, string> = {};
    // Check if at least one ID is provided
    const hasAnyId = aliasForm.ids.some((item) => item.id);

    if (!hasAnyId) {
      errors.general = 'At least one discography ID must be provided';
    }

    // Check if alias name is provided
    if (!aliasForm.aliasName.trim()) {
      errors.aliasName = 'Alias name is required';
    }

    onAliasChange((prev) => ({
      ...prev,
      errors,
    }));

    return Object.keys(errors).length === 0;
  };

  const validateBandForm = () => {
    const errors: Record<string, string> = {};
    
    // If linking to existing band, only validate linked alias and selected band
    if (bandForm.bandMode === 'existing') {
      if (!bandForm.linkedAliasId) {
        errors.missing_alias_link = 'Group needs to have a linked alias';
      }
      if (!bandForm.selectedExistingBandId) {
        errors.selectedExistingBandId = 'Please select an existing band';
      }
      onBandChange((prev) => ({
        ...prev,
        errors,
      }));
      return Object.keys(errors).length === 0;
    }

    // For new bands, validate all fields
    // Check if at least one ID is provided
    const hasAnyId = bandForm.ids.some((item) => item.id);

    if (!hasAnyId) {
      errors.general = 'At least one discography ID must be provided';
    }

    // Check if alias name is provided
    if (!bandForm.stageName.trim()) {
      errors.aliasName = 'Group name is required';
    }

    if (!bandForm.linkedAliasId) {
      errors.missing_alias_link = 'Group needs to have a linked alias';
    }

    onBandChange((prev) => ({
      ...prev,
      errors,
    }));

    return Object.keys(errors).length === 0;
  };

  const handleAddAliasSubmit = () => {
    if (!clientId) return;

    // Validate form
    if (!validateAliasForm()) {
      return;
    }

    // Add to artist data (mock implementation)
    const newArtistData = [];
    // Group IDs by platform for storage
    const groupedIds = {
      discogs: [] as any[],
      deezer: [] as any[],
      spotify: [] as any[],
      musicbrainz: [] as any[],
    };
    aliasForm.ids.forEach((item) => {
      console.log(item, 'item');
      if (item.id) {
        groupedIds[item.source as keyof typeof groupedIds].push({
          id: item.id,
          original_name: item.artistName,
          valid: item.valid,
          approved: item.approved ?? false,
          rejected: item.rejected ?? false,
        } as any);
      }
    });
    // Create a new artist entry
    const newArtist = {
      id: 1,
      name: aliasForm.aliasName,
      ids: {
        discogs:
          groupedIds.discogs.length > 0
            ? {
                ids: groupedIds.discogs,
              }
            : null,
        deezer:
          groupedIds.deezer.length > 0
            ? {
                ids: groupedIds.deezer,
              }
            : null,
        spotify:
          groupedIds.spotify.length > 0
            ? {
                ids: groupedIds.spotify,
              }
            : null,
        musicbrainz:
          groupedIds.musicbrainz.length > 0
            ? {
                ids: groupedIds.musicbrainz,
              }
            : null,
      },
    };
    newArtistData.push(newArtist);

    if (aliasForm.id) {
      api
        .updateAliasArtistAlias({
          userId: clientId,
          aliasId: aliasForm.id,
          artistAliasUpdate: {
            stageName: aliasForm.aliasName,
            manualStageName: aliasForm.manualStageName,
            sourceIds: {
              discogs:
                groupedIds.discogs.length > 0
                  ? groupedIds.discogs
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              deezer:
                groupedIds.deezer.length > 0
                  ? groupedIds.deezer
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              spotify:
                groupedIds.spotify.length > 0
                  ? groupedIds.spotify
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              musicbrainz:
                groupedIds.musicbrainz.length > 0
                  ? groupedIds.musicbrainz
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
            },
          },
        })
        .then(() => {
          getCollectedDiscographyIds(clientId);

          // Close modal and reset form
          onClose();
          onAliasChange({
            aliasName: '',
            isGroup: false,
            isPermanentMember: false,
            ids: [
              {
                source: 'discogs',
                itemId: 'discogs-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'deezer',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'spotify',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'musicbrainz',
                itemId: 'musicbrainz-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
            ],
            manualStageName: false,
            hasAutoPopulated: false,
            errors: {},
          });
        });
    } else {
      api
        .createAliasArtistAlias({
          userId: clientId,
          artistAliasCreate: {
            stageName: aliasForm.aliasName,
            manualStageName: aliasForm.manualStageName,
            sourceIds: {
              discogs:
                groupedIds.discogs.length > 0
                  ? groupedIds.discogs
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              deezer:
                groupedIds.deezer.length > 0
                  ? groupedIds.deezer
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              spotify:
                groupedIds.spotify.length > 0
                  ? groupedIds.spotify
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              musicbrainz:
                groupedIds.musicbrainz.length > 0
                  ? groupedIds.musicbrainz
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
            },
          },
        })
        .then(() => {
          getCollectedDiscographyIds(clientId);

          // Close modal and reset form
          onClose();
          onAliasChange({
            aliasName: '',
            isGroup: false,
            isPermanentMember: false,
            ids: [
              {
                source: 'discogs',
                itemId: 'discogs-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'deezer',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'spotify',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'musicbrainz',
                itemId: 'musicbrainz-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
            ],
            manualStageName: false,
            hasAutoPopulated: false,
            errors: {},
          });
        });
    }
  };

  const handleAddBandSubmit = () => {
    if (!clientId) return;

    // If linking to an existing band, first link the alias, then update if needed
    if (bandForm.bandMode === 'existing' && bandForm.selectedExistingBandId && bandForm.linkedAliasId) {
      console.log('=== Linking alias to existing band ===');
      console.log('Band ID:', bandForm.selectedExistingBandId);
      console.log('Alias ID:', bandForm.linkedAliasId);
      console.log('Is Permanent Member:', bandForm.isPermanentMember);
      console.log('Is Main Performer:', bandForm.isMainPerformer);
      console.log('Form IDs count:', bandForm.ids.length);
      console.log('Form IDs:', bandForm.ids);
      
      // Step 1: Link the alias to the band
      api
        .linkAliasToBandArtistBand({
          userId: clientId,
          bandId: bandForm.selectedExistingBandId,
          linkAliasToBand: {
            aliasId: bandForm.linkedAliasId,
            isPermanentMember: bandForm.isPermanentMember,
            isMainPerformer: bandForm.isMainPerformer,
          },
        })
        .then((): Promise<unknown> => {
          console.log('✅ Successfully linked alias to band');
          
          // Step 2: Check if there are any new source IDs to add/update
          // Group IDs by platform
          const groupedIds = {
            discogs: [] as any[],
            deezer: [] as any[],
            spotify: [] as any[],
            musicbrainz: [] as any[],
          };

          bandForm.ids.forEach((item) => {
            if (item.id && item.id.trim() !== '') {
              groupedIds[item.source as keyof typeof groupedIds].push({
                id: item.id,
                original_name: item.artistName,
                valid: item.valid,
                approved: item.approved ?? false,
                rejected: item.rejected ?? false,
              } as any);
            }
          });

          console.log('Grouped source IDs:', {
            discogs: groupedIds.discogs.length,
            deezer: groupedIds.deezer.length,
            spotify: groupedIds.spotify.length,
            musicbrainz: groupedIds.musicbrainz.length,
          });
          console.log('Grouped source IDs details:', groupedIds);

          // Check if there are any source IDs to update
          const hasSourceIds = 
            groupedIds.discogs.length > 0 ||
            groupedIds.deezer.length > 0 ||
            groupedIds.spotify.length > 0 ||
            groupedIds.musicbrainz.length > 0;

          // Step 3: Update the band (name, permanent member, source IDs)
          if (bandForm.selectedExistingBandId) {
            console.log('📝 Updating band with form data');
            const updatePayload = {
              stageName: bandForm.stageName,
              manualStageName: bandForm.manualStageName,
              isPermanentMember: bandForm.isPermanentMember,
              isMainPerformer: bandForm.isMainPerformer,
              sourceIds: {
                discogs:
                  groupedIds.discogs.length > 0
                    ? groupedIds.discogs
                        .map((id) => ({
                          id: id.id,
                          originalName: id.original_name,
                          valid: id.valid,
                          approved: id.approved,
                          rejected: id.rejected,
                        }))
                    : undefined,
                deezer:
                  groupedIds.deezer.length > 0
                    ? groupedIds.deezer
                        .map((id) => ({
                          id: id.id,
                          originalName: id.original_name,
                          valid: id.valid,
                          approved: id.approved,
                          rejected: id.rejected,
                        }))
                    : undefined,
                spotify:
                  groupedIds.spotify.length > 0
                    ? groupedIds.spotify
                        .map((id) => ({
                          id: id.id,
                          originalName: id.original_name,
                          valid: id.valid,
                          approved: id.approved,
                          rejected: id.rejected,
                        }))
                    : undefined,
                musicbrainz:
                  groupedIds.musicbrainz.length > 0
                    ? groupedIds.musicbrainz
                        .map((id) => ({
                          id: id.id,
                          originalName: id.original_name,
                          valid: id.valid,
                          approved: id.approved,
                          rejected: id.rejected,
                        }))
                    : undefined,
              },
            };
            console.log('Update payload:', JSON.stringify(updatePayload, null, 2));
            return api.updateBandArtistBand({
              userId: clientId,
              bandId: bandForm.selectedExistingBandId,
              artistBandUpdate: updatePayload,
            });
          } else {
            return Promise.resolve();
          }
        })
        .then(() => {
          console.log('✅ Successfully completed linking and updating band');
          getCollectedDiscographyIds(clientId);
          onClose();
          // Reset form
          onBandChange({
            stageName: '',
            isPermanentMember: false,
            isMainPerformer: false,
            linkedAliasId: null,
            bandMode: 'new',
            addGroupStep: undefined,
            groupSourceId: undefined,
            selectedExistingBandId: undefined,
            ids: [
              {
                source: 'discogs',
                itemId: 'discogs-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'deezer',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'spotify',
                itemId: 'spotify-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'musicbrainz',
                itemId: 'musicbrainz-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
            ],
            manualStageName: false,
            hasAutoPopulated: false,
            errors: {},
          });
        })
        .catch((error) => {
          console.error('Error linking alias to band or updating band:', error);
          onBandChange((prev) => ({
            ...prev,
            errors: {
              ...prev.errors,
              general: 'Failed to link alias to band or update band. Please try again.',
            },
          }));
        });
      return;
    }

    // Validate form
    if (!validateBandForm()) {
      console.log('❌ Band form validation failed');
      return;
    }

    const bandMode = bandForm.bandMode || 'new';
    console.log('=== Creating new band ===');
    console.log('Band Mode:', bandMode);
    console.log('Stage Name:', bandForm.stageName);
    console.log('Linked Alias ID:', bandForm.linkedAliasId);
    console.log('Is Permanent Member:', bandForm.isPermanentMember);
    console.log('Is Main Performer:', bandForm.isMainPerformer);
    console.log('Manual Stage Name:', bandForm.manualStageName);
    console.log('Form IDs count:', bandForm.ids.length);

    // Group IDs by platform for storage
    const groupedIds = {
      discogs: [] as any[],
      deezer: [] as any[],
      spotify: [] as any[],
      musicbrainz: [] as any[],
    };

    bandForm.ids.forEach((item) => {
      if (item.id) {
        groupedIds[item.source as keyof typeof groupedIds].push({
          id: item.id,
          original_name: item.artistName,
          valid: item.valid,
          approved: item.approved ?? false,
          rejected: item.rejected ?? false,
        } as any);
      }
    });

    console.log('Grouped source IDs:', {
      discogs: groupedIds.discogs.length,
      deezer: groupedIds.deezer.length,
      spotify: groupedIds.spotify.length,
      musicbrainz: groupedIds.musicbrainz.length,
    });
    console.log('Grouped source IDs details:', groupedIds);

    if (bandForm.id) {
      api
        .updateBandArtistBand({
          userId: clientId,
          bandId: bandForm.id,
          artistBandUpdate: {
            stageName: bandForm.stageName,
            manualStageName: bandForm.manualStageName,
            isPermanentMember: bandForm.isPermanentMember,
            isMainPerformer: bandForm.isMainPerformer,
            linkedAliasId: bandForm.linkedAliasId ?? null,
            sourceIds: {
              discogs:
                groupedIds.discogs.length > 0
                  ? groupedIds.discogs
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              deezer:
                groupedIds.deezer.length > 0
                  ? groupedIds.deezer
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              spotify:
                groupedIds.spotify.length > 0
                  ? groupedIds.spotify
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
              musicbrainz:
                groupedIds.musicbrainz.length > 0
                  ? groupedIds.musicbrainz
                      .filter((id) => !!id.id)
                      .map((id) => ({
                        id: id.id,
                        originalName: id.original_name,
                        valid: id.valid,
                        approved: id.approved,
                        rejected: id.rejected,
                      }))
                  : undefined,
            },
          },
        })
        .then(() => {
          getCollectedDiscographyIds(clientId);

          // Close modal and reset form
          onClose();
          onBandChange({
            stageName: '',
            isPermanentMember: false,
            isMainPerformer: false,
            linkedAliasId: null,
            bandMode: 'new',
            addGroupStep: undefined,
            groupSourceId: undefined,
            selectedExistingBandId: undefined,
            ids: [
              {
                source: 'discogs',
                itemId: 'discogs-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'deezer',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'spotify',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'musicbrainz',
                itemId: 'musicbrainz-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
            ],
            manualStageName: false,
            hasAutoPopulated: false,
            errors: {},
          });
        });
    } else {
      // Creating a new band
      const createPayload = {
        stageName: bandForm.stageName,
        manualStageName: bandForm.manualStageName,
        isPermanentMember: bandForm.isPermanentMember,
        isMainPerformer: bandForm.isMainPerformer,
        linkedAliasId: bandForm.linkedAliasId ?? null,
        sourceIds: {
          discogs:
            groupedIds.discogs.length > 0
              ? groupedIds.discogs
                  .filter((id) => !!id.id)
                  .map((id) => ({
                    id: id.id,
                    originalName: id.original_name,
                    valid: id.valid,
                    approved: id.approved,
                    rejected: id.rejected,
                  }))
              : undefined,
          deezer:
            groupedIds.deezer.length > 0
              ? groupedIds.deezer
                  .filter((id) => !!id.id)
                  .map((id) => ({
                    id: id.id,
                    originalName: id.original_name,
                    valid: id.valid,
                    approved: id.approved,
                    rejected: id.rejected,
                  }))
              : undefined,
          spotify:
            groupedIds.spotify.length > 0
              ? groupedIds.spotify
                  .filter((id) => !!id.id)
                  .map((id) => ({
                    id: id.id,
                    originalName: id.original_name,
                    valid: id.valid,
                    approved: id.approved,
                    rejected: id.rejected,
                  }))
              : undefined,
          musicbrainz:
            groupedIds.musicbrainz.length > 0
              ? groupedIds.musicbrainz
                  .filter((id) => !!id.id)
                  .map((id) => ({
                    id: id.id,
                    originalName: id.original_name,
                    valid: id.valid,
                    approved: id.approved,
                    rejected: id.rejected,
                  }))
              : undefined,
        },
      };
      
      console.log('📝 Creating new band with payload:');
      console.log('Payload:', JSON.stringify(createPayload, null, 2));
      console.log('Linked Alias ID in payload:', createPayload.linkedAliasId);
      
      api
        .createBandArtistBand({
          userId: clientId,
          artistBandCreate: createPayload,
        })
        .then(() => {
          console.log('✅ Successfully created new band with linked alias');
          getCollectedDiscographyIds(clientId);

          // Close modal and reset form
          onClose();
          onBandChange({
            stageName: '',
            isPermanentMember: false,
            isMainPerformer: false,
            linkedAliasId: null,
            bandMode: 'new',
            addGroupStep: undefined,
            groupSourceId: undefined,
            selectedExistingBandId: undefined,
            ids: [
              {
                source: 'discogs',
                itemId: 'discogs-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'deezer',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'spotify',
                itemId: 'deezer-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
              {
                source: 'musicbrainz',
                itemId: 'musicbrainz-0',
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              },
            ],
            manualStageName: false,
            hasAutoPopulated: false,
            errors: {},
          });
        });
    }
  };

  const handleAliasNameChange = (newName: string) => {
    onAliasChange((prev) => {
      if (prev.aliasName !== newName && prev.manualStageName === false) {
        prev.manualStageName = true;
      }

      return {
        ...prev,
        aliasName: newName,
      };
    });
  };

  const handleBandNameChange = (newName: string) => {
    onBandChange((prev) => {
      if (prev.stageName !== newName && prev.manualStageName === false) {
        prev.manualStageName = true;
      }

      return {
        ...prev,
        stageName: newName,
      };
    });
  };

  const handleBandPermanentChange = (isPermanentMember: boolean) => {
    onBandChange((prev) => {
      return {
        ...prev,
        isPermanentMember,
      };
    });
  };

  const handleBandLinkedAliasChange = (linkedAliasId: string) => {
    onBandChange((prev) => {
      return {
        ...prev,
        errors: {
          ...prev.errors,
          missing_alias_id: linkedAliasId
            ? undefined
            : prev.errors.missing_alias_id,
        },
        linkedAliasId: linkedAliasId ?? null,
      };
    });
  };

  const handleNewBandFromSourceId = (sourceId: string, band?: { id: string; stageName: string; sourceIds?: string } | null) => {
    onBandChange((prev) => {
      const ids = [...(prev.ids || [])];
      const discogsIndex = ids.findIndex((id) => id.source === 'discogs');
      const musicbrainzIndex = ids.findIndex((id) => id.source === 'musicbrainz');
      if (discogsIndex >= 0) {
        ids[discogsIndex] = { ...ids[discogsIndex], id: sourceId };
      } else if (musicbrainzIndex >= 0) {
        ids[musicbrainzIndex] = { ...ids[musicbrainzIndex], id: sourceId };
      }
      return {
        ...prev,
        bandMode: 'new',
        addGroupStep: 'form',
        selectedExistingBandId: undefined,
        stageName: band?.stageName ?? prev.stageName ?? '',
        manualStageName: !!band?.stageName,
        ids,
        errors: { ...prev.errors, general: undefined },
      };
    });
  };

  const handleSkipSourceIdLookup = () => {
    onBandChange((prev) => ({
      ...prev,
      bandMode: 'new',
      addGroupStep: 'form',
      selectedExistingBandId: undefined,
      errors: { ...prev.errors, general: undefined },
    }));
  };

  const handleBandModeChange = (mode: 'new' | 'existing') => {
    onBandChange((prev) => {
      // If switching from existing to new, reset the form to default state
      if (prev.bandMode === 'existing' && mode === 'new') {
        return {
          stageName: '',
          isPermanentMember: false,
          isMainPerformer: false,
          linkedAliasId: prev.linkedAliasId, // Keep the linked alias
          bandMode: 'new',
          ids: [
            {
              source: 'discogs',
              itemId: 'discogs-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
            {
              source: 'deezer',
              itemId: 'deezer-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
            {
              source: 'spotify',
              itemId: 'spotify-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
            {
              source: 'musicbrainz',
              itemId: 'musicbrainz-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
          ],
          manualStageName: false,
          hasAutoPopulated: false,
          errors: {},
          selectedExistingBandId: undefined,
        };
      }
      
      return {
        ...prev,
        bandMode: mode,
        // Clear selected band when switching to new mode
        selectedExistingBandId: mode === 'new' ? undefined : prev.selectedExistingBandId,
      };
    });
  };

  const handleExistingBandChange = (bandId: string, unlinkedBandData?: { bandId: string; name: string; sourceId?: string; source?: string }) => {
    console.log('=== handleExistingBandChange START ===');
    console.log('bandId:', bandId);
    console.log('unlinkedBandData:', unlinkedBandData);
    console.log('clientId:', clientId);
    console.log('collectedDiscographyIds length:', collectedDiscographyIds.length);
    
    // If bandId is empty, reset the form while preserving linkedAliasId and bandMode
    if (!bandId) {
      console.log('bandId is empty, resetting form while preserving linkedAliasId and bandMode');
      onBandChange((prev) => {
        const currentBandMode = prev.bandMode || 'existing';
        const currentLinkedAliasId = prev.linkedAliasId;
        
        return {
          ...prev,
          selectedExistingBandId: undefined,
          stageName: '',
          isPermanentMember: false,
          isMainPerformer: false,
          manualStageName: false,
          hasAutoPopulated: false,
          errors: {},
          linkedAliasId: currentLinkedAliasId, // Preserve linked alias
          bandMode: currentBandMode, // Preserve band mode
          ids: [
            {
              source: 'discogs',
              itemId: 'discogs-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
            {
              source: 'deezer',
              itemId: 'deezer-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
            {
              source: 'spotify',
              itemId: 'spotify-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
            {
              source: 'musicbrainz',
              itemId: 'musicbrainz-0',
              id: '',
              valid: false,
              approved: true,
              rejected: false,
              created_by_system: false,
            },
          ],
        };
      });
      return;
    }
    
    if (!clientId) {
      console.warn('No clientId, returning early');
      return;
    }

    // First try to find the selected band from collectedDiscographyIds (if it's already linked to current artist)
    const selectedBand = collectedDiscographyIds.find(
      (band) => band.id === bandId && band.type === 'group'
    );
    console.log('selectedBand from collectedDiscographyIds:', selectedBand);
    console.log('selectedBand source_ids:', selectedBand?.source_ids);

    // If found in collectedDiscographyIds, load the full band data with all source IDs
    if (selectedBand) {
      console.log('Found band in collectedDiscographyIds, loading full data');
      const isCreateMode = !bandForm.id;
      loadBandDataIntoForm(selectedBand, { bandMode: 'existing', ...(isCreateMode ? { addGroupStep: 'form' as const } : {}) });
      return;
    }

    // If not found in collectedDiscographyIds, use the API to retrieve the full band data
    // This is needed because the band might be linked to a different artist
    console.log('Band not found in collectedDiscographyIds, fetching from API...');
    console.log('Calling api.retrieveBandArtistBand with:', { userId: clientId, bandId });
    
    api
      .retrieveBandArtistBand({
        userId: clientId,
        bandId: bandId,
      })
      .then((bandData) => {
        console.log('=== API Response: retrieveBandArtistBand ===');
        console.log('bandData:', bandData);
        console.log('bandData.id:', bandData.id);
        console.log('bandData.stageName:', bandData.stageName);
        console.log('bandData.sourceIds:', bandData.sourceIds);
        console.log('bandData.sourceIds type:', typeof bandData.sourceIds);
        console.log('bandData type:', typeof bandData);
        console.log('bandData keys:', Object.keys(bandData));
        
        // Parse sourceIds - it might be a JSON string or already an object
        let parsedSourceIds: any = {
          discogs: [],
          deezer: [],
          spotify: [],
          musicbrainz: [],
        };
        
        if (bandData.sourceIds) {
          if (typeof bandData.sourceIds === 'string') {
            try {
              parsedSourceIds = JSON.parse(bandData.sourceIds);
              console.log('Parsed sourceIds from string:', parsedSourceIds);
            } catch (e) {
              console.error('Error parsing sourceIds as JSON:', e);
              console.log('sourceIds string value:', bandData.sourceIds);
            }
          } else if (typeof bandData.sourceIds === 'object') {
            parsedSourceIds = bandData.sourceIds;
            console.log('sourceIds is already an object:', parsedSourceIds);
          }
        }
        
        // Ensure parsedSourceIds has the expected structure
        if (!parsedSourceIds.discogs) parsedSourceIds.discogs = [];
        if (!parsedSourceIds.deezer) parsedSourceIds.deezer = [];
        if (!parsedSourceIds.spotify) parsedSourceIds.spotify = [];
        if (!parsedSourceIds.musicbrainz) parsedSourceIds.musicbrainz = [];
        
        // Convert ArtistBand to the format expected by loadBandDataIntoForm
        // ArtistBand has a different structure than DiscographyId, so we need to transform it
        const transformedBand = {
          id: bandData.id,
          stageName: bandData.stageName,
          manualStageName: bandData.manualStageName,
          type: 'group',
          source_ids: parsedSourceIds,
          is_permanent_member: bandData.isPermanentMember === 'true' || Boolean(bandData.isPermanentMember) === true,
          is_main_performer: bandData.isMainPerformer === 'true' || Boolean(bandData.isMainPerformer) === true,
          linked_alias_id: bandData.linkedAliasId,
          created_by_system: bandData.createdBySystem || false,
        };
        
        console.log('Transformed band data:', transformedBand);
        console.log('Transformed band source_ids:', transformedBand.source_ids);
        console.log('Transformed band source_ids structure:', JSON.stringify(transformedBand.source_ids, null, 2));
        
        const isCreateMode = !bandForm.id;
        loadBandDataIntoForm(transformedBand, { bandMode: 'existing', ...(isCreateMode ? { addGroupStep: 'form' as const } : {}) });
      })
      .catch((error) => {
        console.error('Error fetching band from API:', error);
        // Fallback to unlinked band data if API call fails
        if (unlinkedBandData) {
          console.log('Using unlinked band data as fallback after API error:', unlinkedBandData);
          const isCreateMode = !bandForm.id;
          loadUnlinkedBandDataIntoForm(bandId, unlinkedBandData, { bandMode: 'existing', ...(isCreateMode ? { addGroupStep: 'form' as const } : {}) });
        } else {
          console.error('No unlinkedBandData available and API call failed');
        }
      });
  };

  const loadBandDataIntoForm = (selectedBand: any, extraState?: Partial<BandForm>) => {
    console.log('=== loadBandDataIntoForm START ===');
    console.log('selectedBand:', selectedBand);
    console.log('selectedBand.id:', selectedBand.id);
    console.log('selectedBand.stageName:', selectedBand.stageName);
    console.log('selectedBand.type:', selectedBand.type);
    console.log('selectedBand.source_ids:', selectedBand.source_ids);
    console.log('selectedBand.source_ids type:', typeof selectedBand.source_ids);
    console.log('selectedBand.source_ids is null/undefined:', selectedBand.source_ids == null);
    
    if (selectedBand.source_ids) {
      console.log('selectedBand.source_ids keys:', Object.keys(selectedBand.source_ids));
      console.log('selectedBand.source_ids structure:', JSON.stringify(selectedBand.source_ids, null, 2));
    }
    
    // Check if source_ids exists and has the expected structure
    if (!selectedBand.source_ids) {
      console.error('ERROR: selectedBand.source_ids is missing!');
      console.error('selectedBand keys:', Object.keys(selectedBand));
      return;
    }
    
    // Helper function to deduplicate IDs by id value within each source
    const deduplicateIds = (ids: any[], source: string): any[] => {
      const seen = new Set<string>();
      return ids.filter((id) => {
        const idKey = id.id || '';
        if (idKey && seen.has(idKey)) {
          console.log(`Removing duplicate ${source} ID:`, idKey);
          return false;
        }
        if (idKey) {
          seen.add(idKey);
        }
        return true;
      });
    };
    
    // Convert the band's source IDs to BandId format
    const newIds: BandId[] = [];
    console.log('Initialized newIds array:', newIds);

    // Add Discogs IDs
    const discogsIdsRaw = selectedBand.source_ids.discogs || [];
    const discogsIds = deduplicateIds(discogsIdsRaw, 'discogs');
    console.log('=== Processing Discogs IDs ===');
    console.log('discogsIdsRaw (before dedup):', discogsIdsRaw);
    console.log('discogsIds (after dedup):', discogsIds);
    console.log('discogsIds type:', typeof discogsIds);
    console.log('discogsIds is array:', Array.isArray(discogsIds));
    console.log('discogsIds count:', discogsIds.length);
    
    if (discogsIds.length > 0) {
      discogsIds.forEach((id: any, index: number) => {
        console.log(`Processing discogs ID ${index}:`, id);
        const bandId: BandId = {
          source: 'discogs',
          itemId: `existing-discogs-${index}`,
          id: id.id,
          artistName: id.original_name,
          validated: true,
          valid: id.valid,
          approved: id.approved,
          rejected: id.rejected,
          created_by_system: id.created_by_system,
        };
        console.log(`Created discogs BandId ${index}:`, bandId);
        newIds.push(bandId);
      });
    } else {
      // Add empty placeholder if no discogs IDs exist
      console.log('No discogs IDs, adding empty placeholder');
      newIds.push({
        source: 'discogs',
        itemId: 'existing-discogs-0',
        id: '',
        valid: false,
        approved: true,
        rejected: false,
        created_by_system: false,
      });
    }
    console.log('After discogs, newIds count:', newIds.length);

    // Add Deezer IDs
    const deezerIdsRaw = selectedBand.source_ids.deezer || [];
    const deezerIds = deduplicateIds(deezerIdsRaw, 'deezer');
    console.log('=== Processing Deezer IDs ===');
    console.log('deezerIdsRaw (before dedup):', deezerIdsRaw);
    console.log('deezerIds (after dedup):', deezerIds);
    console.log('deezerIds type:', typeof deezerIds);
    console.log('deezerIds is array:', Array.isArray(deezerIds));
    console.log('deezerIds count:', deezerIds.length);
    
    if (deezerIds.length > 0) {
      deezerIds.forEach((id: any, index: number) => {
        console.log(`Processing deezer ID ${index}:`, id);
        const bandId: BandId = {
          source: 'deezer',
          itemId: `existing-deezer-${index}`,
          id: id.id,
          artistName: id.original_name,
          validated: true,
          valid: id.valid,
          approved: id.approved,
          rejected: id.rejected,
          created_by_system: id.created_by_system,
        };
        console.log(`Created deezer BandId ${index}:`, bandId);
        newIds.push(bandId);
      });
    } else {
      // Add empty placeholder if no deezer IDs exist
      console.log('No deezer IDs, adding empty placeholder');
      newIds.push({
        source: 'deezer',
        itemId: 'existing-deezer-0',
        id: '',
        valid: false,
        approved: true,
        rejected: false,
        created_by_system: false,
      });
    }
    console.log('After deezer, newIds count:', newIds.length);

    // Add Spotify IDs
    const spotifyIdsRaw = selectedBand.source_ids.spotify || [];
    const spotifyIds = deduplicateIds(spotifyIdsRaw, 'spotify');
    console.log('=== Processing Spotify IDs ===');
    console.log('spotifyIdsRaw (before dedup):', spotifyIdsRaw);
    console.log('spotifyIds (after dedup):', spotifyIds);
    console.log('spotifyIds type:', typeof spotifyIds);
    console.log('spotifyIds is array:', Array.isArray(spotifyIds));
    console.log('spotifyIds count:', spotifyIds.length);
    
    if (spotifyIds.length > 0) {
      spotifyIds.forEach((id: any, index: number) => {
        console.log(`Processing spotify ID ${index}:`, id);
        const bandId: BandId = {
          source: 'spotify',
          itemId: `existing-spotify-${index}`,
          id: id.id,
          artistName: id.original_name,
          validated: true,
          valid: id.valid,
          approved: id.approved,
          rejected: id.rejected,
          created_by_system: id.created_by_system,
        };
        console.log(`Created spotify BandId ${index}:`, bandId);
        newIds.push(bandId);
      });
    } else {
      // Add empty placeholder if no spotify IDs exist
      console.log('No spotify IDs, adding empty placeholder');
      newIds.push({
        source: 'spotify',
        itemId: 'existing-spotify-0',
        id: '',
        valid: false,
        approved: true,
        rejected: false,
        created_by_system: false,
      });
    }
    console.log('After spotify, newIds count:', newIds.length);

    // Add MusicBrainz IDs
    const musicbrainzIdsRaw = selectedBand.source_ids.musicbrainz || [];
    const musicbrainzIds = deduplicateIds(musicbrainzIdsRaw, 'musicbrainz');
    console.log('=== Processing MusicBrainz IDs ===');
    console.log('musicbrainzIdsRaw (before dedup):', musicbrainzIdsRaw);
    console.log('musicbrainzIds (after dedup):', musicbrainzIds);
    console.log('musicbrainzIds type:', typeof musicbrainzIds);
    console.log('musicbrainzIds is array:', Array.isArray(musicbrainzIds));
    console.log('musicbrainzIds count:', musicbrainzIds.length);
    
    if (musicbrainzIds.length > 0) {
      musicbrainzIds.forEach((id: any, index: number) => {
        console.log(`Processing musicbrainz ID ${index}:`, id);
        const bandId: BandId = {
          source: 'musicbrainz',
          itemId: `existing-musicbrainz-${index}`,
          id: id.id,
          artistName: id.original_name,
          validated: true,
          valid: id.valid,
          approved: id.approved,
          rejected: id.rejected,
          created_by_system: id.created_by_system,
        };
        console.log(`Created musicbrainz BandId ${index}:`, bandId);
        newIds.push(bandId);
      });
    } else {
      // Add empty placeholder if no musicbrainz IDs exist
      console.log('No musicbrainz IDs, adding empty placeholder');
      newIds.push({
        source: 'musicbrainz',
        itemId: 'existing-musicbrainz-0',
        id: '',
        valid: false,
        approved: true,
        rejected: false,
        created_by_system: false,
      });
    }
    console.log('After musicbrainz, newIds count:', newIds.length);
    console.log('Final newIds array:', newIds);

    // Ensure all sources have at least one ID field (this should already be handled above, but keep as safety check)
    const sources: DiscographySourceType[] = ['discogs', 'deezer', 'spotify', 'musicbrainz'];
    sources.forEach((source) => {
      if (!newIds.some((id) => id.source === source)) {
        console.log(`No ${source} IDs found, adding empty placeholder`);
        newIds.push({
          source: source,
          itemId: `existing-${source}-0`,
          id: '',
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        });
      }
    });
    
    // Legacy check - should not be needed anymore since we handle each source above
    if (newIds.length === 0) {
      console.log('WARNING: No source IDs found, adding empty placeholders');
      newIds.push(
        {
          source: 'discogs',
          itemId: 'existing-discogs-0',
          id: '',
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        },
        {
          source: 'deezer',
          itemId: 'existing-deezer-0',
          id: '',
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        },
        {
          source: 'spotify',
          itemId: 'existing-spotify-0',
          id: '',
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        },
        {
          source: 'musicbrainz',
          itemId: 'existing-musicbrainz-0',
          id: '',
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        }
      );
    }

    console.log('=== Preparing to update form ===');
    console.log('newIds created:', newIds);
    console.log('newIds length:', newIds.length);
    console.log('newIds structure:', JSON.stringify(newIds, null, 2));
    console.log('Updating form with:', {
      selectedExistingBandId: selectedBand.id,
      stageName: selectedBand.stageName,
      idsCount: newIds.length,
      manualStageName: selectedBand.manualStageName,
      isPermanentMember: selectedBand.is_permanent_member ?? false,
      isMainPerformer: selectedBand.is_main_performer ?? false,
    });

    onBandChange((prev) => {
      console.log('=== onBandChange callback START ===');
      console.log('Previous form state:', prev);
      console.log('Previous form ids:', prev.ids);
      console.log('Previous form ids length:', prev.ids?.length || 0);
      
      const updated = {
        ...prev,
        selectedExistingBandId: selectedBand.id,
        ids: newIds,
        stageName: selectedBand.stageName,
        manualStageName: selectedBand.manualStageName,
        isPermanentMember: selectedBand.is_permanent_member ?? false,
        isMainPerformer: selectedBand.is_main_performer ?? false,
        // Keep other properties unchanged
        ...(typeof extraState === 'object' && extraState !== null ? extraState : {}),
      };
      
      console.log('Updated form state:', updated);
      console.log('Updated form ids:', updated.ids);
      console.log('Updated form ids length:', updated.ids?.length || 0);
      console.log('onBandChange type:', typeof onBandChange);
      console.log('=== onBandChange callback END ===');
      return updated;
    });
    console.log('=== loadBandDataIntoForm END ===');
  };

  const loadUnlinkedBandDataIntoForm = (bandId: string, unlinkedBandData: { bandId: string; name: string; sourceId?: string; source?: string }, extraState?: Partial<BandForm>) => {
    console.log('loadUnlinkedBandDataIntoForm called with:', { bandId, unlinkedBandData });
    const newIds: BandId[] = [];
    
    // If we have sourceId from unlinked band, add it
    if (unlinkedBandData.sourceId && unlinkedBandData.source) {
      const source = unlinkedBandData.source.toLowerCase() as DiscographySourceType;
      console.log('Normalized source:', source);
      console.log('Is valid source:', ['discogs', 'deezer', 'spotify', 'musicbrainz'].includes(source));
      
      if (['discogs', 'deezer', 'spotify', 'musicbrainz'].includes(source)) {
        const bandId: BandId = {
          source: source,
          itemId: `existing-${source}-0`,
          id: unlinkedBandData.sourceId,
          validated: true,
          valid: true,
          approved: true,
          rejected: false,
          created_by_system: false,
        };
        console.log('Created BandId from unlinked data:', bandId);
        newIds.push(bandId);
      } else {
        console.warn('Invalid source type:', source);
      }
    } else {
      console.log('No sourceId or source in unlinkedBandData');
    }
    
    // Add empty placeholders for other sources
    console.log('Adding empty placeholders for other sources');
    const sources: DiscographySourceType[] = ['discogs', 'deezer', 'spotify', 'musicbrainz'];
    sources.forEach((source) => {
      if (!newIds.some((id) => id.source === source)) {
        const placeholderId: BandId = {
          source: source,
          itemId: `existing-${source}-0`,
          id: '',
          valid: false,
          approved: true,
          rejected: false,
          created_by_system: false,
        };
        console.log(`Adding placeholder for ${source}:`, placeholderId);
        newIds.push(placeholderId);
      } else {
        console.log(`Skipping placeholder for ${source} (already exists)`);
      }
    });

    console.log('=== Final newIds for unlinked band ===');
    console.log('newIds:', newIds);
    console.log('newIds length:', newIds.length);
    console.log('newIds structure:', JSON.stringify(newIds, null, 2));
    console.log('Updating form with unlinked band data:', {
      selectedExistingBandId: bandId,
      stageName: unlinkedBandData.name,
      idsCount: newIds.length,
    });

    onBandChange((prev) => {
      console.log('=== onBandChange callback for unlinked band START ===');
      console.log('Previous form state:', prev);
      console.log('Previous form ids:', prev.ids);
      console.log('Previous form ids length:', prev.ids?.length || 0);
      
      const updated = {
        ...prev,
        selectedExistingBandId: bandId,
        ids: newIds,
        stageName: unlinkedBandData.name,
        manualStageName: false,
        ...(typeof extraState === 'object' && extraState !== null ? extraState : {}),
      };
      
      console.log('Updated form state:', updated);
      console.log('Updated form ids:', updated.ids);
      console.log('Updated form ids length:', updated.ids?.length || 0);
      console.log('onBandChange type:', typeof onBandChange);
      console.log('=== onBandChange callback for unlinked band END ===');
      return updated;
    });
    console.log('=== loadUnlinkedBandDataIntoForm END ===');
  };

  const handleAliasRemoveItem = (itemToRemove: AliasId) => {
    onAliasChange((prev) => {
      const lastItemForSource =
        prev.ids.filter((item) => item.source === itemToRemove.source).length <
        2;

      return {
        ...prev,
        ids: [
          ...prev.ids.filter((item) => item.itemId !== itemToRemove.itemId),
          ...(lastItemForSource
            ? [
                {
                  source: itemToRemove.source,
                  itemId: `${itemToRemove.source}-${prev.ids.length}`,
                  id: '',
                  valid: false,
                  approved: true,
                  rejected: false,
                  created_by_system: false,
                },
              ]
            : []),
        ],
      };
    });
  };

  const handleBandRemoveItem = (itemToRemove: AliasId) => {
    onBandChange((prev) => {
      const lastItemForSource =
        prev.ids.filter((item) => item.source === itemToRemove.source).length <
        2;

      return {
        ...prev,
        ids: [
          ...prev.ids.filter((item) => item.itemId !== itemToRemove.itemId),
          ...(lastItemForSource
            ? [
                {
                  source: itemToRemove.source,
                  itemId: `${itemToRemove.source}-${prev.ids.length}`,
                  id: '',
                  valid: false,
                  approved: true,
                  rejected: false,
                  created_by_system: false,
                },
              ]
            : []),
        ],
      };
    });
  };

  return (
    <>
      <AliasModal
        show={showAliasModal}
        onClose={onClose}
        formData={aliasForm}
        mode={aliasForm.id ? 'edit' : 'create'}
        activeTab={activeAliasTab}
        onTabChange={onAliasTabChange}
        onNameChange={handleAliasNameChange}
        onItemsChange={handleAliasFormChange}
        onRemoveItem={handleAliasRemoveItem}
        onAddIdField={addAliasSourceField}
        onSubmit={handleAddAliasSubmit}
      />
      <BandModal
        show={showBandModal}
        onClose={onClose}
        formData={bandForm}
        mode={bandForm.id ? 'edit' : 'create'}
        activeTab={activeBandTab}
        onTabChange={onBandTabChange}
        onNameChange={handleBandNameChange}
        onItemsChange={handleBandFormChange}
        onRemoveItem={handleBandRemoveItem}
        onAddIdField={addBandSourceField}
        onPermanentMemberChange={handleBandPermanentChange}
        onLinkedAliasChange={handleBandLinkedAliasChange}
        onSubmit={handleAddBandSubmit}
        onCreateNewAlias={onCreateNewAlias}
        onBandModeChange={handleBandModeChange}
        onExistingBandChange={(bandId, unlinkedBandData) => {
          console.log('=== onExistingBandChange prop called ===');
          console.log('bandId:', bandId);
          console.log('unlinkedBandData:', unlinkedBandData);
          console.log('handleExistingBandChange function:', handleExistingBandChange);
          handleExistingBandChange(bandId, unlinkedBandData);
        }}
        onNewBandFromSourceId={handleNewBandFromSourceId}
        onSkipSourceIdLookup={handleSkipSourceIdLookup}
      />
    </>
  );
}

export default AliasBandManagement;
